import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, ChevronLeft, ChevronRight } from 'lucide-react';
import VideoLightbox, { VideoItem } from '@/components/VideoLightbox';
import { extractYouTubeId, youtubeThumbnail, youtubeEmbed } from '@/lib/youtube';

interface DbVideoItem {
  id: string;
  title: string;
  description: string | null;
  source_type: 'upload' | 'youtube';
  video_url: string;
  poster_url: string | null;
}

const YT_AUTOPLAY_DURATION_MS = 12000;

const getPoster = (item: DbVideoItem): string | null => {
  if (item.poster_url) return item.poster_url;
  if (item.source_type === 'youtube') {
    const id = extractYouTubeId(item.video_url);
    return id ? youtubeThumbnail(id) : null;
  }
  return null;
};

const VideoGallery = () => {
  const { t } = useLanguage();
  const [items, setItems] = useState<DbVideoItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [current, setCurrent] = useState(0);
  const ytTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const fetchVideos = async () => {
      const { data } = await supabase
        .from('video_gallery_items')
        .select('id, title, description, source_type, video_url, poster_url')
        .eq('visible', true)
        .order('sort_order', { ascending: true });
      if (data) setItems(data as DbVideoItem[]);
      setLoaded(true);
    };
    fetchVideos();
  }, []);

  const next = () => setCurrent((i) => (items.length ? (i + 1) % items.length : 0));
  const prev = () =>
    setCurrent((i) => (items.length ? (i - 1 + items.length) % items.length : 0));

  // Auto-advance for YouTube via fallback timer
  useEffect(() => {
    if (!items.length || lightboxOpen) return;
    const item = items[current];
    if (!item) return;
    if (ytTimerRef.current) {
      window.clearTimeout(ytTimerRef.current);
      ytTimerRef.current = null;
    }
    if (item.source_type === 'youtube') {
      ytTimerRef.current = window.setTimeout(() => {
        next();
      }, YT_AUTOPLAY_DURATION_MS);
    }
    return () => {
      if (ytTimerRef.current) {
        window.clearTimeout(ytTimerRef.current);
        ytTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, items, lightboxOpen]);

  if (loaded && items.length === 0) return null;

  const lightboxVideos: VideoItem[] = items.map((i) => ({
    id: i.id,
    title: i.title,
    description: i.description,
    source_type: i.source_type,
    video_url: i.video_url,
  }));

  const item = items[current];
  const poster = item ? getPoster(item) : null;
  const ytId = item?.source_type === 'youtube' ? extractYouTubeId(item.video_url) : null;

  return (
    <section id="video-gallery" className="py-20 md:py-32 px-4">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2
            className="text-5xl md:text-7xl tracking-wider text-foreground"
            style={{ fontFamily: 'Bebas Neue' }}
          >
            {t.videoGallery.title}
          </h2>
          <p className="mt-4 text-muted-foreground text-lg">{t.videoGallery.subtitle}</p>
        </motion.div>

        {!loaded ? (
          <div className="flex justify-center">
            <div
              className="bg-card/50 rounded-sm animate-pulse w-[320px] md:w-[360px]"
              style={{ aspectRatio: '9 / 16' }}
            />
          </div>
        ) : (
          <div className="relative">
            {/* Carousel stage */}
            <div className="flex items-center justify-center gap-4">
              {/* Prev button */}
              <button
                type="button"
                onClick={prev}
                aria-label="Předchozí video"
                className="hidden md:flex shrink-0 w-12 h-12 rounded-full bg-card/70 backdrop-blur-sm border border-border/60 hover:border-primary/70 hover:bg-card text-foreground hover:text-primary items-center justify-center transition-colors duration-300 z-10"
              >
                <ChevronLeft size={24} />
              </button>

              <div
                className="relative w-full max-w-[320px] md:max-w-[360px]"
                style={{ aspectRatio: '9 / 16' }}
              >
                <AnimatePresence mode="wait">
                  {item && (
                    <motion.button
                      key={item.id}
                      type="button"
                      onClick={() => setLightboxOpen(true)}
                      initial={{ opacity: 0, scale: 0.96 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.96 }}
                      transition={{ duration: 0.4 }}
                      className="group absolute inset-0 block w-full overflow-hidden rounded-sm bg-black ring-1 ring-border/50 hover:ring-primary/60 transition-all duration-300 shadow-[0_20px_50px_-20px_hsl(var(--primary)/0.45)]"
                      aria-label={item.title || `Video ${current + 1}`}
                    >
                      {item.source_type === 'upload' ? (
                        <video
                          src={item.video_url}
                          poster={poster ?? undefined}
                          autoPlay
                          muted
                          playsInline
                          preload="metadata"
                          onEnded={next}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      ) : ytId ? (
                        <iframe
                          src={youtubeEmbed(ytId) + '&mute=1&playsinline=1'}
                          title={item.title || `Video ${current + 1}`}
                          allow="autoplay; encrypted-media; picture-in-picture"
                          allowFullScreen
                          className="absolute inset-0 w-full h-full pointer-events-none"
                        />
                      ) : poster ? (
                        <img
                          src={poster}
                          alt={item.title || `Video ${current + 1}`}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      ) : (
                        <div className="absolute inset-0 bg-card" />
                      )}

                      {/* Play overlay */}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/10 transition-colors duration-300 pointer-events-none">
                        <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary/90 backdrop-blur-sm flex items-center justify-center shadow-[0_0_30px_hsl(var(--primary)/0.6)] group-hover:scale-110 transition-transform duration-300">
                          <Play size={28} className="text-primary-foreground ml-1" fill="currentColor" />
                        </div>
                      </div>

                      {/* Title */}
                      {item.title && (
                        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/85 via-black/40 to-transparent pointer-events-none">
                          <h3
                            className="text-white font-bold text-lg md:text-xl drop-shadow-lg tracking-wide"
                            style={{ fontFamily: 'Bebas Neue' }}
                          >
                            {item.title}
                          </h3>
                        </div>
                      )}
                    </motion.button>
                  )}
                </AnimatePresence>
              </div>

              {/* Next button */}
              <button
                type="button"
                onClick={next}
                aria-label="Další video"
                className="hidden md:flex shrink-0 w-12 h-12 rounded-full bg-card/70 backdrop-blur-sm border border-border/60 hover:border-primary/70 hover:bg-card text-foreground hover:text-primary items-center justify-center transition-colors duration-300 z-10"
              >
                <ChevronRight size={24} />
              </button>
            </div>

            {/* Mobile arrows */}
            <div className="flex md:hidden justify-center gap-4 mt-6">
              <button
                type="button"
                onClick={prev}
                aria-label="Předchozí video"
                className="w-11 h-11 rounded-full bg-card/70 backdrop-blur-sm border border-border/60 hover:border-primary/70 text-foreground hover:text-primary flex items-center justify-center transition-colors duration-300"
              >
                <ChevronLeft size={22} />
              </button>
              <button
                type="button"
                onClick={next}
                aria-label="Další video"
                className="w-11 h-11 rounded-full bg-card/70 backdrop-blur-sm border border-border/60 hover:border-primary/70 text-foreground hover:text-primary flex items-center justify-center transition-colors duration-300"
              >
                <ChevronRight size={22} />
              </button>
            </div>

            {/* Pagination dots */}
            {items.length > 1 && (
              <div className="flex justify-center gap-2 mt-6 flex-wrap">
                {items.map((_, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setCurrent(idx)}
                    aria-label={`Přejít na video ${idx + 1}`}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      idx === current
                        ? 'bg-primary w-8'
                        : 'bg-border hover:bg-muted-foreground w-2'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <VideoLightbox
        videos={lightboxVideos}
        currentIndex={current}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onNext={() => setCurrent((i) => (i + 1) % lightboxVideos.length)}
        onPrev={() =>
          setCurrent((i) => (i - 1 + lightboxVideos.length) % lightboxVideos.length)
        }
      />
    </section>
  );
};

export default VideoGallery;
